import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
const GUEST_CART_KEY = "pp_guest_cart";

function getGuestCart() {
  try { return JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || []; } catch { return []; }
}
function saveGuestCart(items) {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [guestCart, setGuestCart] = useState(getGuestCart);

  const fetchCartCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart`, { withCredentials: true });
      setCartCount(res.data.items?.length || 0);
    } catch {}
  }, []);

  const updateGuestCartCount = useCallback(() => {
    const gc = getGuestCart();
    setGuestCart(gc);
    setCartCount(gc.length);
  }, []);

  const mergeGuestCartToServer = useCallback(async () => {
    const gc = getGuestCart();
    if (gc.length === 0) return;
    for (const item of gc) {
      try {
        await axios.post(`${API}/cart`, { product_id: item.product_id, quantity: item.quantity, pack_size: item.pack_size }, { withCredentials: true });
      } catch {}
    }
    localStorage.removeItem(GUEST_CART_KEY);
    setGuestCart([]);
    await fetchCartCount();
  }, [fetchCartCount]);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      fetchCartCount();
    } catch {
      setUser(null);
      updateGuestCartCount();
    } finally {
      setLoading(false);
    }
  }, [fetchCartCount, updateGuestCartCount]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const addToCart = async (product, quantity = 1, packSize = "") => {
    const ps = packSize || (product.pack_sizes?.[0]?.size || "");
    if (user) {
      const res = await axios.post(`${API}/cart`, { product_id: product.product_id, quantity, pack_size: ps }, { withCredentials: true });
      setCartCount(res.data.items?.length || 0);
      return res.data;
    } else {
      const gc = getGuestCart();
      const idx = gc.findIndex(i => i.product_id === product.product_id);
      if (idx >= 0) {
        gc[idx].quantity += quantity;
      } else {
        gc.push({
          item_id: `guest_${Date.now()}`,
          product_id: product.product_id,
          name: product.name,
          image: product.images?.[0] || "",
          price: product.sale_price,
          mrp: product.mrp,
          quantity,
          pack_size: ps,
          requires_prescription: product.requires_prescription || false
        });
      }
      saveGuestCart(gc);
      setGuestCart(gc);
      setCartCount(gc.length);
      return { items: gc, total: gc.reduce((s, i) => s + i.price * i.quantity, 0) };
    }
  };

  const updateGuestItem = (itemId, qty) => {
    let gc = getGuestCart();
    if (qty <= 0) { gc = gc.filter(i => i.item_id !== itemId); }
    else { gc = gc.map(i => i.item_id === itemId ? { ...i, quantity: qty } : i); }
    saveGuestCart(gc);
    setGuestCart(gc);
    setCartCount(gc.length);
  };

  const removeGuestItem = (itemId) => {
    const gc = getGuestCart().filter(i => i.item_id !== itemId);
    saveGuestCart(gc);
    setGuestCart(gc);
    setCartCount(gc.length);
  };

  const getCart = async () => {
    if (user) {
      const res = await axios.get(`${API}/cart`, { withCredentials: true });
      return res.data;
    }
    const gc = getGuestCart();
    return { items: gc, total: gc.reduce((s, i) => s + i.price * i.quantity, 0) };
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(res.data);
    await mergeGuestCartToServer();
    await fetchCartCount();
    return res.data;
  };

  const register = async (name, email, phone, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, phone, password }, { withCredentials: true });
    setUser(res.data);
    await mergeGuestCartToServer();
    return res.data;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const res = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(res.data);
    await mergeGuestCartToServer();
    await fetchCartCount();
    return res.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    setUser(null);
    setCartCount(getGuestCart().length);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, loginWithGoogle, processGoogleSession, logout, cartCount, setCartCount, fetchCartCount, addToCart, getCart, guestCart, updateGuestItem, removeGuestItem }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
